// src/backend/hooks/feed/useFeed.ts
import { useState, useEffect } from "react";
import { useUser } from "../user/use-user";
import { FeedService } from "../../services/feed/FeedService";
import { Post, Media } from "../../../models/Post";

export const useFeed = (category: string = 'all', userIdsFilter?: string[]) => {
  const [posts, setPosts] = useState<Post[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [lastVisible, setLastVisible] = useState<any>(null); // For pagination
  const [hasMore, setHasMore] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const fetchPosts = async (reset: boolean = false) => {
    try {
      setLoading(true);
      setError(null);

      const lastDoc = reset ? null : lastVisible;

      // Ensure lastDoc is not undefined on initial load or subsequent ones
      const result = await FeedService.getPosts(lastDoc, category, 10, userIdsFilter);
      
      const fetchedPosts = result.posts;
      setLastVisible(result.lastVisible);
      setHasMore(fetchedPosts.length > 0);

      if (reset) {
        setPosts(fetchedPosts);
      } else {
        setPosts(prev => [...prev, ...fetchedPosts]);
      }
    } catch (err: any) {
      setError(err.message || 'Failed to fetch posts');
    } finally {
      setLoading(false);
    }
  };

  const likePost = async (postId: string, userId: string, isLiked: boolean) => {
    try {
      // Optimistic update
      setPosts(prevPosts => 
        prevPosts.map(post => {
          if (post.id === postId) {
            return {
              ...post,
              likes: isLiked ? post.likes - 1 : post.likes + 1,
              likedBy: isLiked 
                ? post.likedBy?.filter(id => id !== userId) 
                : [...(post.likedBy || []), userId]
            };
          }
          return post;
        })
      );
      
      await FeedService.toggleLike(postId, userId, isLiked);
    } catch (err) {
      // Revert if API fails
      // Silent error or toast?
      console.error(err);
    }
  };

  const deletePost = async (postId: string) => {
    try {
      setPosts(prev => prev.filter(post => post.id !== postId));
      await FeedService.deletePost(postId);
    } catch (err: any) {
      setError(err.message || 'Failed to delete post');
    }
  };


  useEffect(() => {
    fetchPosts(true); // Initial load
  }, [category, userIdsFilter ? userIdsFilter.join(',') : '']); // Re-fetch when category or filter changes

  return {
    posts,
    loading,
    error,
    hasMore,
    fetchMore: () => fetchPosts(false),
    refresh: () => fetchPosts(true),
    likePost,
    deletePost
  };
};

export const useCreatePost = () => {
  const [uploading, setUploading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const { loggedInUser } = useUser();

  const createPost = async (
    content: string, 
    tag: string,
    title?: string,
    mediaFiles?: Array<{file: File, type: 'image'|'video'|'pdf'}>,
    linkPreview?: any,
    userRole?: 'Business Owner' | 'Employee' | 'Student',
    affiliation?: string
  ) => {
    if (!loggedInUser) {
      throw new Error("You must be logged in to create a post");
    }

    try {
      setUploading(true);
      setError(null);
      
      await FeedService.createPost(
        loggedInUser.userId || '',
        loggedInUser.name || 'Anonymous',
        loggedInUser.displayName || 'user', // Handle username logic
        loggedInUser.profilePicture || loggedInUser.profilePhotoUrl || '',
        content,
        tag,
        title,
        mediaFiles,
        linkPreview,
        userRole,
        affiliation
      );
      
    } catch (err: any) {
      setError(err.message || 'Failed to create post');
      throw err;
    } finally {
      setUploading(false);
    }
  };

  return { createPost, uploading, error };
};
